import { useState, useRef, useEffect } from "react";
import { gsap } from "gsap";
import { getContainersList } from "../../services/container";
import Cookies from "js-cookie";
import { CropImage } from "../../components/utils/ImageTreatment";

const ContainerDetailsPanel = ({
  isOpen,
  onClose,
  containerIndex,
  containerConfig,
  alertState,
  visibility,
  containerData,
  onSaveData,
}) => {
  const panelRef = useRef(null);
  const overlayRef = useRef(null);
  const [activeTab, setActiveTab] = useState("info");
  const [comments, setComments] = useState(containerData?.comments || []);
  const [images, setImages] = useState(containerData?.images || []);
  const [newComment, setNewComment] = useState("");
  const [newImageUrl, setNewImageUrl] = useState("");
  const [containerId, setContainerId] = useState(
    containerData?.containerId || "",
  );
  const [monitoringData, setMonitoringData] = useState(null);
  const [containersList, setContainersList] = useState([]);
  const [isLoadingContainers, setIsLoadingContainers] = useState(false);

  useEffect(() => {
    if (containerData) {
      const newContainerId = containerData.containerId || "";
      setComments(containerData.comments || []);
      setImages(containerData.images || []);
      setContainerId(newContainerId);
      setMonitoringData(null); // Réinitialiser les données de monitoring quand on change de conteneur
    } else {
      setContainerId("");
      setMonitoringData(null);
    }
  }, [containerData]);

  useEffect(() => {
    if (activeTab === "monitoring" && containersList.length === 0) {
      fetchContainersList();
    }
  }, [activeTab, containersList.length]);

  useEffect(() => {
    if (containerId && activeTab === "monitoring") {
      fetchMonitoringData(containerId);
    } else {
      setMonitoringData(null);
    }
  }, [containerId, activeTab]);

  const fetchContainersList = async () => {
    setIsLoadingContainers(true);
    try {
      const token = Cookies.get("userToken");
      if (!token) {
        console.error("No token available");
        setIsLoadingContainers(false);
        return;
      }

      const response = await getContainersList({
        page: null,
        limit: null,
        fieldSort: "filling_level",
        orderSort: "-1",
        keywordFilter: JSON.stringify([]),
        arrayFilterFillingLevel: JSON.stringify([0, 100]),
        typeFillingFilter: "all",
      });

      if (response.status === 200 && response.data.data) {
        setContainersList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching containers list:", error);
    } finally {
      setIsLoadingContainers(false);
    }
  };

  const fetchMonitoringData = async (id) => {
    if (!id) {
      setMonitoringData(null);
      return;
    }

    try {
      const token = Cookies.get("userToken");
      if (!token) {
        console.error("No token available");
        return;
      }

      // Filtrer par ID de conteneur
      const filters = [
        {
          key: id,
          cat: "ID",
          type: "_id",
        },
      ];

      const response = await getContainersList({
        page: null,
        limit: null,
        fieldSort: "filling_level",
        orderSort: "-1",
        keywordFilter: JSON.stringify(filters),
        arrayFilterFillingLevel: JSON.stringify([0, 100]),
        typeFillingFilter: "all",
      });

      if (
        response.status === 200 &&
        response.data.data &&
        response.data.data.length > 0
      ) {
        const container = response.data.data[0];

        setMonitoringData({
          ...container,
          fillingLevel: container.filling_level || 0,
          lastUpdate:
            container._time ||
            container.lastUpdate ||
            container.updatedAt ||
            new Date().toISOString(),
          status: container.activatedFillingLevelOption ? "active" : "inactive",
        });
      } else {
        setMonitoringData(null);
      }
    } catch (error) {
      console.error("Error fetching monitoring data:", error);
      setMonitoringData(null);
    }
  };

  const handleContainerSelect = (selectedId) => {
    setContainerId(selectedId);
    const updatedData = {
      ...containerData,
      containerId: selectedId,
      comments: containerData?.comments || [],
      images: containerData?.images || [],
    };
    onSaveData(updatedData);
  };

  const getContainerDisplayName = (container) => {
    const wasteName =
      container.wasteName ||
      container.contentCategoryJoin?.label ||
      "Non spécifié";
    const positioning = container.positioning || "";
    return positioning ? `${wasteName} - ${positioning}` : wasteName;
  };

  useEffect(() => {
    if (isOpen) {
      gsap.fromTo(
        panelRef.current,
        { x: 500, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.4, ease: "power3.out" },
      );
      gsap.fromTo(
        overlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.3 },
      );
    } else {
      gsap.to(panelRef.current, {
        x: 500,
        opacity: 0,
        duration: 0.3,
        ease: "power3.in",
      });
      gsap.to(overlayRef.current, {
        opacity: 0,
        duration: 0.2,
      });
    }
  }, [isOpen]);

  const handleAddComment = () => {
    if (!newComment.trim()) return;
    const comment = {
      id: Date.now(),
      text: newComment,
      author: "Utilisateur",
      date: new Date().toISOString(),
    };
    const updated = [...comments, comment];
    setComments(updated);
    onSaveData({ ...containerData, comments: updated, images });
    setNewComment("");
  };

  const handleAddImage = () => {
    if (!newImageUrl.trim()) return;
    const image = {
      id: Date.now(),
      url: newImageUrl,
      caption: "",
      date: new Date().toISOString(),
    };
    const updated = [...images, image];
    setImages(updated);
    onSaveData({ ...containerData, comments, images: updated });
    setNewImageUrl("");
  };

  const handleDeleteComment = (id) => {
    const updated = comments.filter((c) => c.id !== id);
    setComments(updated);
    onSaveData({ ...containerData, comments: updated, images });
  };

  const handleDeleteImage = (id) => {
    const updated = images.filter((img) => img.id !== id);
    setImages(updated);
    onSaveData({ ...containerData, comments, images: updated });
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        ref={overlayRef}
        className="fixed inset-0 bg-black bg-opacity-30 z-40"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col"
        style={{ transform: "translateX(500px)" }}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-[var(--akanthas-cyan)] to-[#008a8a] text-white">
          <div>
            <h2 className="text-2xl font-bold">
              Conteneur {containerIndex !== null ? containerIndex + 1 : ""}
            </h2>
            <p className="text-sm opacity-90 mt-1">
              {containerConfig?.dimensions?.length}m ×{" "}
              {containerConfig?.dimensions?.width}m ×{" "}
              {containerConfig?.dimensions?.height}m
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors p-2 rounded-full hover:bg-white hover:bg-opacity-20"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto flex flex-col">
          <div className="border-b border-gray-200 flex">
            <button
              onClick={() => setActiveTab("info")}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === "info"
                  ? "text-[var(--akanthas-cyan)] border-b-2 border-[var(--akanthas-cyan)]"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <i className="fas fa-info-circle mr-2"></i>
              Informations
            </button>
            <button
              onClick={() => setActiveTab("monitoring")}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === "monitoring"
                  ? "text-[var(--akanthas-cyan)] border-b-2 border-[var(--akanthas-cyan)]"
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              <i className="fas fa-chart-line mr-2"></i>
              Monitoring
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {activeTab === "info" && (
              <>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">
                      Position
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="font-mono text-sm text-gray-800">
                        X: {containerConfig?.position.x.toFixed(2)}
                        <br />
                        Y: {containerConfig?.position.y.toFixed(2)}
                        <br />
                        Z: {containerConfig?.position.z.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 mb-1">État alerte</p>
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            alertState ? "bg-red-500" : "bg-gray-300"
                          }`}
                        />
                        <p className="text-sm font-medium">
                          {alertState ? "Active" : "Inactive"}
                        </p>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-600 mb-1">Visibilité</p>
                      <p className="text-sm font-medium">
                        {visibility ? "Visible" : "Masqué"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Commentaires ({comments.length})
                  </h3>
                  <div className="space-y-3 mb-4">
                    {comments.map((comment) => (
                      <div
                        key={comment.id}
                        className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="text-sm font-medium text-gray-800">
                              {comment.author}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(comment.date).toLocaleDateString(
                                "fr-FR",
                              )}
                            </p>
                          </div>
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                        <p className="text-sm text-gray-700">{comment.text}</p>
                      </div>
                    ))}
                    {comments.length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">
                        Aucun commentaire pour le moment
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Ajouter un commentaire..."
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--akanthas-cyan)]"
                      onKeyPress={(e) =>
                        e.key === "Enter" && handleAddComment()
                      }
                    />
                    <button
                      onClick={handleAddComment}
                      className="px-4 py-2 bg-[var(--akanthas-cyan)] text-white rounded-lg hover:bg-[var(--akanthas-yellow)] transition-colors"
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Images ({images.length})
                  </h3>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {images.map((image) => (
                      <div key={image.id} className="relative group">
                        <img
                          src={image.url}
                          alt={image.caption || "Image"}
                          className="w-full h-32 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/200?text=Image+Error";
                          }}
                        />
                        <button
                          onClick={() => handleDeleteImage(image.id)}
                          className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <i className="fas fa-times text-xs"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                      placeholder="URL de l'image..."
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--akanthas-cyan)]"
                      onKeyPress={(e) => e.key === "Enter" && handleAddImage()}
                    />
                    <button
                      onClick={handleAddImage}
                      className="px-4 py-2 bg-[var(--akanthas-cyan)] text-white rounded-lg hover:bg-[var(--akanthas-yellow)] transition-colors"
                    >
                      <i className="fas fa-image"></i>
                    </button>
                  </div>
                </div>
              </>
            )}

            {activeTab === "monitoring" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Lier au monitoring
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Conteneur de monitoring
                      </label>
                      {isLoadingContainers ? (
                        <div className="flex items-center justify-center py-4">
                          <i className="fas fa-spinner fa-spin text-[var(--akanthas-cyan)] text-xl"></i>
                          <span className="ml-2 text-sm text-gray-600">
                            Chargement des conteneurs...
                          </span>
                        </div>
                      ) : (
                        <select
                          value={containerId}
                          onChange={(e) =>
                            handleContainerSelect(e.target.value)
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--akanthas-cyan)] bg-white"
                        >
                          <option value="">Sélectionner un conteneur</option>
                          {containersList.map((container) => (
                            <option key={container._id} value={container._id}>
                              {getContainerDisplayName(container)}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                </div>

                {containerId && monitoringData && (
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                      Données de monitoring
                    </h3>
                    <div className="space-y-4">
                      {monitoringData.image_url && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm font-medium text-gray-700 mb-3">
                            Dernière photo de remplissage
                          </p>
                          <div className="border border-gray-300 rounded-lg overflow-hidden">
                            <CropImage
                              container={monitoringData}
                              data={monitoringData}
                              imgDivWidth={400}
                              imgDivHeight={300}
                              isTooltip={true}
                              isMap={false}
                              isCharacterization={false}
                              displayLegend={false}
                              materialsDropdownToDisplay={[]}
                              showCheckbox={false}
                            />
                          </div>
                          {monitoringData._time && (
                            <p className="text-xs text-gray-500 mt-2">
                              Photo prise le{" "}
                              {new Date(monitoringData._time).toLocaleString(
                                "fr-FR",
                              )}
                            </p>
                          )}
                        </div>
                      )}

                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <p className="text-sm font-medium text-gray-700">
                            Niveau de remplissage actuel
                          </p>
                          <p className="text-lg font-bold text-[var(--akanthas-cyan)]">
                            {monitoringData.fillingLevel}%
                          </p>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-4">
                          <div
                            className="bg-[var(--akanthas-cyan)] h-4 rounded-full transition-all"
                            style={{ width: `${monitoringData.fillingLevel}%` }}
                          ></div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-600 mb-1">Statut</p>
                          <p className="text-sm font-medium">
                            {monitoringData.status === "active" ? (
                              <span className="text-green-600">Actif</span>
                            ) : (
                              <span className="text-gray-600">Inactif</span>
                            )}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-600 mb-1">
                            Dernière mise à jour
                          </p>
                          <p className="text-sm font-medium">
                            {monitoringData.lastUpdate
                              ? new Date(
                                  monitoringData.lastUpdate,
                                ).toLocaleString("fr-FR")
                              : "N/A"}
                          </p>
                        </div>
                      </div>

                      {monitoringData.wasteName && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-600 mb-1">
                            Flux de matière
                          </p>
                          <p className="text-sm font-medium">
                            {monitoringData.wasteName}
                          </p>
                        </div>
                      )}

                      {monitoringData.positioning && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-600 mb-1">Position</p>
                          <p className="text-sm font-medium">
                            {monitoringData.positioning}
                          </p>
                        </div>
                      )}

                      {monitoringData.addressName && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-600 mb-1">Adresse</p>
                          <p className="text-sm font-medium">
                            {monitoringData.addressName}
                          </p>
                        </div>
                      )}

                      {monitoringData.weightKG && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-600 mb-1">Poids</p>
                          <p className="text-sm font-medium">
                            {monitoringData.weightKG}{" "}
                            {monitoringData.weightUnit || "kg"}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {containerId && !monitoringData && (
                  <div className="text-center py-8">
                    <i className="fas fa-spinner fa-spin text-3xl text-gray-400 mb-2"></i>
                    <p className="text-sm text-gray-500">
                      Chargement des données...
                    </p>
                  </div>
                )}

                {!containerId && (
                  <div className="text-center py-8">
                    <i className="fas fa-link text-3xl text-gray-400 mb-2"></i>
                    <p className="text-sm text-gray-500">
                      Veuillez lier un Container ID pour voir les données de
                      monitoring
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ContainerDetailsPanel;
